import { DEFAULT_GROUP_COVER_IMAGE, DEFAULT_GROUP_LOGO, NO_GROUP_SLUG } from '@/helper/const'
import GroupMember, { GroupMemberRole, GroupMemberStatus, IGroupMember } from '@/models/GroupMember'
import Groups, { GroupType, IGroup, IUserGroups, MemberStatus, NewsletterConfigUI } from '@/models/Groups'
import { getSessionCookieData, isGokollab } from '@ghl/clientportal-core/init'
import { computed, reactive, ref, watch } from 'vue'

import { UserState } from '@/composition/users'
import config from '@/config'
import { isChannelRestricted } from '@/helper/channel.helper'
import { emitter } from '@/main'
import { ChannelVisibility } from '@/models/Channels'
import { EventName } from '@/models/TrackEvent'
import { GroupsService } from '@/service/GroupsService'
import { ListingsService } from '@/service/ListingsService'
import { IPaginationParams } from '@/service/types'
import { UsersService } from '@/service/UsersService'
import { useGlobalStore } from '@/store/app'
import { waitForAuthResolved } from '@/util/storeHelpers'
import { AxiosError } from 'axios'
import { AnalyticsState } from './analytics'
import { FormState } from './forms'
import { LeaderboardState } from './leaderboard'

export enum SideBarList {
  GROUPS = 'GROUPS',
  CHANNELS = 'CHANNELS',
}
export enum GroupAccessType {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}
export interface Group {
  id: string
  slug: string
  name: string
  logo: any
  coverImage: string
  membersCount: number
  type: GroupType
}

export function waitForGroupData(): Promise<void> {
  /*
  Wait for the group data to be loaded.
  */
  return new Promise(resolve => {
    if (GroupState.state.groupData && Object.keys(GroupState.state.groupData).length > 0) {
      resolve()
      return
    }

    const unwatch = watch(
      () => GroupState.state.groupData,
      groupData => {
        if (groupData && Object.keys(groupData).length > 0) {
          unwatch()
          resolve()
        }
      },
      { immediate: true }
    )
  })
}
// Utility to patch group.settings with required defaults
function patchGroupSettingsDefaults(group: any) {
  if (!group.settings) {
    group.settings = {}
  }
  if (group.settings.groupType === undefined) {
    group.settings.groupType = GroupType.PUBLIC
  }
  if (group.settings.membershipQuestionsEnabled === undefined) {
    group.settings.membershipQuestionsEnabled = false
  }
  // Add more defaults as needed
  return group
}

export class GroupsState {
  state: any = reactive({
    currentGroup: null as IGroup | null,
    userGroup: {} as any,
    processing: false,
    newlyJoined: false,
    noCommunities: false,
    fetching: false,
    fetchingGroupData: false,
    fetchingPinnedGroups: false,
    fetchingUserGroup: false,
    allGroupsFetched: false,
    groupList: [] as any,
    pinnedGroupList: [] as Group[],
    unpinnedGroupList: [] as Group[],
    pinnedGroupIds: [] as string[],
    uniqueGroupsMap: new Map<number | string, any>(),
    groupData: {} as any,
    updatedGroupData: {} as any,
    newsletterConfigUI: null as NewsletterConfigUI | null,
    membersLevelDistribution: new Map<string, number>(),
    isGroupDataUpdated: Boolean,
    groupOwner: {} as any,
    showGroupSwitcherDrawer: false,
    groupType: computed(() => {
      return GroupState.state?.groupData?.settings?.groupType === GroupType.PRIVATE ? GroupType.PRIVATE : GroupType.PUBLIC
    }),
    channelList: [] as any,
    restrictedChannelList: [] as any, // channels where user cannot post
    nonRestrictedChannelList: [] as any, // channels where user can post
    showChannelList: false,
    role: null as any,
    orderId: null as any,
    userStatus: null as any,
    userMembershipAnswers: null as any,
    editorCurrentChannel: null as any, // this is used to store the current channel where post or comment editors are opened
    isGroupAdmin: computed(() => {
      return (GroupState.state.role === 'ADMIN' || GroupState.state.role === 'OWNER') && GroupState.state.userStatus === MemberStatus.ACTIVE
    }),
    isUserActiveGroupMember: computed(() => {
      return GroupState.state.userStatus === MemberStatus.ACTIVE
    }),
    userNotInitiatedJoinGroupProcess: computed(() => {
      return !GroupState.state.userStatus || GroupState.state.userStatus == MemberStatus.PAYMENT_INITIATED
    }),
    userInJoinGroupProcess: computed(() => {
      return GroupState.state.userStatus === MemberStatus.REQUESTED || GroupState.state.userStatus === MemberStatus.PAYMENT_RECEIVED
    }),
    groupAdmins: new Map<string, IGroupMember>(), // Map<contactId, GroupMember>
    groupMembers: new Array<IGroupMember>(),
    membersPageCurrentFilter: 'All',
    groupLink: computed(() => {
      if (isGokollab()) {
        return `https://${config.GOKOLLAB_DOMAIN}/${this.state.groupData.slug}/home`
      }
      return `https://${window.location.hostname}/communities/groups/${this.state.groupData.slug}/home`
    }),
    requestCount: 0,
    groupMembersAvatarOptions: [],
    closedGroup: ref(false),
    sideBarListView: ref(SideBarList.CHANNELS),
    groupInfoTopPosition: ref(0),
    isGroupColorsUpdated: false,
    routesWithNoBottomNavbar: [
      `group-invitations`,
      `to-review`,
      `communities-settings`,
      `conversations`,
      'chat-view',
      `global-user-profile`,
    ],
    routesWithBackButton: ['profile', 'global-user-profile'],
    currentGroupListFetchPromise: null as Promise<void> | null,
  })

  resetState(): void {
    this.state.groupData = {} as any
    this.state.newsletterConfigUI = null
    this.state.groupMembers = []
    this.state.groupOwner = {} as any
    this.state.role = null as any
    this.state.userStatus = null as any
    this.state.userMembershipAnswers = null as any
    this.state.userGroup = {} as any
    this.state.groupAdmins = new Map<string, IGroupMember>()
    this.state.groupMembers = []
    this.state.membersLevelDistribution = new Map<string, number>()
    this.state.isGroupDataUpdated = Boolean
    this.state.showGroupSwitcherDrawer = false
    this.state.channelList = []
    this.state.fetchingUserGroup = false
  }
  async getGroupBySlug(slug: string): Promise<any> {
    if (slug === NO_GROUP_SLUG || slug === undefined) {
      return
    }
    /*
    Reset the group data for gokollab, because the group can have a different locationId.
    The post service is dependent on the locationId from GroupState.
    */
    try {
      this.state.fetchingGroupData = true
      this.state.channelList = undefined
      const group = await GroupsService.getGroupBySlug(slug)

      /**
       * @description As part of sanitization of gokollab group slugs, redirection is provided for old slugs in order to make the old URLs accessible. This will be depreciated in future once the traffic from old URLs is not required.
       */
      if (isGokollab() && group?.redirect) {
        window.location.href = `${window.location.origin}/${group.redirect}/home?redirected_from_old_slug=true`
        return
      }

      this.state.currentGroup = group.groupDetail

      if (!this.state.uniqueGroupsMap.has(slug)) {
        this.state.uniqueGroupsMap.set(slug, group.groupDetail)
      }
      let isPresentInGroupList = false
      this.state.groupList.forEach(group => {
        if (group.slug === slug) {
          isPresentInGroupList = true
        }
      })
      if (group.groupDetail?.settings?.groupSwitcherVisibility == false && !isPresentInGroupList) {
        this.state.groupList.push(group.groupDetail)
      }
      this.setGroupDataInState(group.groupDetail, false)
      this.state.groupOwner = group.owner
      if (!this.state.groupData.branding) {
        this.state.groupData.branding = {
          mode: 'light',
        }
      } else if (!this.state.groupData.branding?.mode) {
        this.state.groupData.branding.mode = 'light'
      }
      this.setRole((group.user && group.user[0]?.role) || null)
      this.state.userStatus = group.user ? group.user[0]?.status || MemberStatus.ACTIVE : undefined
      this.state.userMembershipAnswers = group.user && group.user[0]?.membershipAnswers
      this.state.userGroup = group.user ? group?.user[0] : undefined
      FormState.state.membershipQuestions = group.membershipQuestions

      return group
    } catch (error) {
      if (!(error instanceof AxiosError && error.response?.status === 404)) {
        console.error(error)
      }
      throw error
    } finally {
      this.state.fetching = false
      this.state.fetchingGroupData = false
      if (GroupState.state.role) {
        AnalyticsState.trackCommunitiesAccessEvent({
          groupId: GroupState.state.groupData?._id,
          status: GroupState.state.userStatus,
          locationId: GroupState.state.groupData?.locationId,
          eventName: EventName.COMMUNITIES_GROUP_ACCESS,
          eventData: {},
          role: GroupState.state.role,
        })
      }
    }
  }

  async setGroupDataInState(groupDetail: any, isUpdated = false): Promise<void> {
    this.state.isGroupColorsUpdated = false
    // Patch groupDetail with settings defaults before assigning
    const patchedGroupDetail = patchGroupSettingsDefaults({ ...groupDetail })
    this.state.groupData = patchedGroupDetail
    this.state.updatedGroupData = JSON.parse(JSON.stringify(patchedGroupDetail))
    this.state.newsletterConfigUI = null

    if (!this.state.updatedGroupData?.settings) {
      this.state.updatedGroupData.settings = {}
    }
    if (!isUpdated) {
      LeaderboardState.clearState()
      UserState.reset()
    }
  }
  async fetchUser(): Promise<void> {
    const cookie = getSessionCookieData()
    useGlobalStore().setUserId(cookie?.contactId)
    await UserState.getCurrentUser()
    useGlobalStore().setAuthenticated(true)
  }
  async getGroupByLocation(query: IPaginationParams, skipCache?: boolean): Promise<void> {
    if (this.state.currentGroupListFetchPromise) {
      return this.state.currentGroupListFetchPromise
    }

    if ((this.state.allGroupsFetched || this.state.fetching) && !skipCache) {
      return
    }

    this.state.currentGroupListFetchPromise = (async () => {
      try {
        this.state.fetching = true

        if (!query.skip && query.skip !== 0 && this.state.groupList.length && !skipCache) {
          query.skip = this.state.groupList.length - 1
        }

        const newGroupList = await GroupsService.getGroupByLocation(query)

        newGroupList.forEach(group => {
          const keyValue = group['slug']
          if (!this.state.uniqueGroupsMap.has(keyValue)) {
            this.state.uniqueGroupsMap.set(keyValue, group)
          }
        })

        this.state.groupList = Array.from(this.state.uniqueGroupsMap.values())

        if (newGroupList.length < query?.limit) {
          this.state.allGroupsFetched = true
        }

        return
      } catch (error) {
        console.error(error)
        throw error
      } finally {
        this.state.fetching = false
        this.state.currentGroupListFetchPromise = null
      }
    })()

    return this.state.currentGroupListFetchPromise
  }
  async joinGroup(role = 'MEMBER'): Promise<void> {
    const appStore = useGlobalStore()
    if (appStore.userId) {
      const status = GroupState.state.groupType === GroupType.PRIVATE ? MemberStatus.REQUESTED : MemberStatus.ACTIVE

      if (UsersService === undefined) {
        await new Promise(resolve => {
          const interval = setInterval(() => {
            if (UsersService) {
              clearInterval(interval)
              resolve(true)
            }
          }, 100)
        })
      }
      const userGroup = await UsersService.joinGroup(role, appStore.userId, status, GroupState.state.orderId)
      if (UserState.state.currentUser) {
        if (!UserState.state.currentUser.contactId) {
          UserState.state.currentUser['contactId'] = userGroup.contactId
        }
      } else if (UserState?.state) {
        UserState.state['currentUser'] = {
          contactId: userGroup.contactId,
        } as any
      }
      GroupState.state.userStatus = userGroup.status
      this.setRole(userGroup.role)

      if (GroupState.state.userStatus === MemberStatus.ACTIVE) {
        GroupState.state.groupData.membersCount += 1
        GroupState.state.newlyJoined = true
      }
      setTimeout(() => {
        GroupState.state.newlyJoined = false
      }, 3000)
    } else {
      return
    }
  }
  async getGroupAdmins(): Promise<void> {
    const groupAdmins = await UsersService.getGroupMembersList([GroupMemberRole.ADMIN, GroupMemberRole.OWNER])

    this.state.groupAdmins = new Map<string, GroupMember>()
    groupAdmins.forEach((groupAdmin: GroupMember) => {
      this.state.groupAdmins.set(groupAdmin.contactId, groupAdmin)
    })
  }
  async getGroupMembersDetails(
    memberRole?: GroupMemberRole,
    memberStatus?: GroupMemberStatus,
    searchText?: string,
    paginationObject?: { limit: number; pageNo: number },
    showEmail?: boolean,
    isMemberFilterSame = false,
    isFetchingForMention = false
  ): Promise<any[]> {
    const groupMembers = await UsersService.getGroupMembersDetails(memberRole, memberStatus, searchText, paginationObject, showEmail)
    if (!isFetchingForMention) {
      if (this.state.groupMembers.length > 0 && isMemberFilterSame) this.state.groupMembers = [...this.state.groupMembers, ...groupMembers]
      else this.state.groupMembers = groupMembers
    }
    return groupMembers
  }
  async getGroupMembersDetailsForChannelSuggestions(
    channelId: string,
    searchText?: string,
    memberRole?: GroupMemberRole,
    paginationObject?: { limit: number; pageNo: number }
  ): Promise<any[]> {
    const groupMembers = await UsersService.getGroupMembersDetailsForChannelSuggestions(channelId, searchText, memberRole, paginationObject)
    return groupMembers
  }
  async fetchGroupMembers(): Promise<void> {
    await this.getGroupMembersDetails(
      undefined,
      GroupMemberStatus.ACTIVE,
      '',
      {
        limit: 10,
        pageNo: 1,
      },
      GroupState.state.isGroupAdmin ? true : false
    )
    this.state.groupMembersAvatarOptions = this.state.groupMembers.map(user => {
      return {
        ...user,
        src: user.avatar,
      }
    })
  }
  async getGroupMemberRequestCount(): Promise<void> {
    try {
      const requestCountResponse = await UsersService.getGroupMemberRequestCount()
      this.state.requestCount = requestCountResponse.count
    } catch (error) {
      console.error(error)
    }
  }
  async createGroup(groupData: IGroup): Promise<void> {
    const groups = new Groups(groupData)
    await groups.save()
  }
  async updateListingSlug(newSlug: string): Promise<void> {
    await ListingsService.updateSlugByExternalId(GroupState.state.groupData._id.toString(), newSlug)
    this.state.groupData.marketplaceGroupSlug = newSlug
    this.setGroupDataInState(this.state.groupData, true)
  }
  async updateGroup(groupData: IGroup): Promise<void> {
    const updatedGroupData: IGroup = { ...groupData }

    if (isGokollab()) {
      updatedGroupData.slug = undefined
    }
    const group = new Groups({ ...updatedGroupData })
    await group.save()
    console.log('group.data', group.data)
    this.setGroupDataInState(group.data, true)
  }
  async deleteGroup(): Promise<void> {}
  async getUserData(slug: string): Promise<any> {
    return await UsersService.getUserDataBySlug(slug)
  }
  async rearrangeChannels(updatedChannelSequence: [{ id: string; channelName: string; sequence: number }]): Promise<void> {
    try {
      await GroupsService.updateChannelSequence(this.state.groupData._id, updatedChannelSequence)
    } catch (e) {
      console.error(e)
    }
  }
  updateShowChannelList(updatedValue: boolean): void {
    this.state.showChannelList = updatedValue
  }
  async getGroupMembersLevelDistribution(): Promise<Map<string, number>> {
    if (this.state.membersLevelDistribution.size > 0) {
      return this.state.membersLevelDistribution
    }
    const response = await GroupsService.getGroupMembersLevelDistribution()
    this.state.membersLevelDistribution = response
    return this.state.membersLevelDistribution
  }
  async submitMembershipAnswers(payload: { answers: (string | boolean[])[] }): Promise<any> {
    const response = await UsersService.submitMembershipAnswer({
      ...payload,
      formId: GroupState?.state?.groupData?.settings?.formId,
      contactId: UserState?.state?.currentUser?.contactId,
    })
    GroupState.state.userMembershipAnswers = {
      success: true,
      answers: payload?.answers,
      formId: GroupState?.state?.groupData?.settings?.formId,
      contactId: UserState?.state?.currentUser?.contactId,
    }
    return response
  }
  isActiveMember(): boolean {
    return this.state.role && this.state.userStatus === MemberStatus.ACTIVE && useGlobalStore().isAuthenticated
  }
  isAdminOrOwner(): boolean {
    return this.isActiveMember() && (this.state.role === 'ADMIN' || this.state.role === 'OWNER')
  }
  async fetchPinnedGroups(): Promise<void> {
    try {
      await waitForAuthResolved()
      const isUserLoggedIn = !!useGlobalStore().isAuthenticated

      if (!isUserLoggedIn) {
        this.state.pinnedGroupList = []
        return
      }

      if (!this.state.fetchingPinnedGroups) {
        this.state.fetchingPinnedGroups = true
        const response = await GroupsService.getPinnedGroups()
        this.state.pinnedGroupList = response
        this.state.fetchingPinnedGroups = false
      }
    } catch (error) {
      console.error(error)
    } finally {
      this.state.fetchingPinnedGroups = false
      await this.computeUnpinnedGroups()
    }
  }
  async computeUnpinnedGroups(): Promise<void> {
    this.state.pinnedGroupIds = this.state.pinnedGroupList.map(group => group.id)
    this.state.unpinnedGroupList = []

    for (let i = 0; i < this.state.groupList.length; i++) {
      const group = this.state.groupList[i]
      const formattedGroup: Group = {
        id: group._id,
        slug: group.slug,
        name: group.name,
        logo: group?.branding?.logo || DEFAULT_GROUP_LOGO,
        coverImage: group?.branding?.cover || DEFAULT_GROUP_COVER_IMAGE,
        membersCount: group?.membersCount || 0,
        type: group?.settings?.groupType?.toLowerCase() === GroupType.PRIVATE.toLowerCase() ? GroupType.PRIVATE : GroupType.PUBLIC,
      }

      if (!this.state.pinnedGroupIds.includes(group._id)) {
        this.state.unpinnedGroupList.push(formattedGroup)
      }
    }
  }

  async pinGroup(groupId: string): Promise<void> {
    await GroupsService.pinGroup(groupId)
    await this.fetchPinnedGroups()
  }

  async unpinGroup(groupId: string): Promise<void> {
    await GroupsService.unpinGroup(groupId)
    await this.fetchPinnedGroups()
  }

  async syncPricing(groupId: string): Promise<void> {
    const response = await GroupsService.syncPricing(groupId)
    if (response?.settings?.membership) {
      this.state.groupData.settings.membership = response.settings.membership
      this.state.updatedGroupData.settings.membership = response.settings.membership
    }
  }

  updateShowGroupSwitcherDrawer(value: boolean): void {
    this.state.showGroupSwitcherDrawer = value
  }
  async initiateGroupPayment(orderId: string, orderSourceId: string): Promise<any> {
    try {
      const response = await GroupsService.initiateGroupPayment(this.state.groupData._id, orderId, orderSourceId)

      GroupState.state.userGroup = response
      return response
    } catch (error) {
      console.error('Error initiating group payment:', error)
      throw error
    }
  }

  async captureGroupPayment(contactId: string, orderId: string, orderSourceId: string): Promise<any> {
    try {
      const response = await GroupsService.captureGroupPayment(this.state.groupData._id, contactId, orderId, orderSourceId)
      // Update user status in state after successful payment capture

      GroupState.state.userGroup = response
      GroupState.state.userStatus = response.status
      this.setRole(response.role)

      return response
    } catch (error) {
      console.error('Error capturing group payment:', error)
      throw error
    }
  }

  setRole(newRole: any): void {
    const previousRole = this.state.role
    this.state.role = newRole

    // Recalculate channel restriction when role changes
    // This is needed because user role is not set in the app.ts file
    if (previousRole === null && newRole !== null) {
      this.separateChannelsByRestriction()
    }
  }

  separateChannelsByRestriction(): void {
    /*
    Distribute channels between restricted and non-restricted lists.
    */
    this.state.restrictedChannelList = []
    this.state.nonRestrictedChannelList = []

    this.state.channelList?.forEach((channel: any) => {
      if (isChannelRestricted(channel)) {
        if (!this.state.restrictedChannelList) {
          this.state.restrictedChannelList = []
        }
        this.state.restrictedChannelList.push(channel)
      } else {
        if (!this.state.nonRestrictedChannelList) {
          this.state.nonRestrictedChannelList = []
        }
        this.state.nonRestrictedChannelList.push(channel)
      }
    })
  }

  setChannels(channels: any[]): void {
    // emit a function indicating that the channels have been set
    emitter.emit('currentGroup:channelsSet', channels)
    this.state.channelList = channels
    this.separateChannelsByRestriction()
  }

  async getGroupOwnedByUserWithPagination(contactId: string, pageNo: number, limit: number): Promise<IUserGroups> {
    return await GroupsService.getGroupOwnedByUserWithPagination(contactId, pageNo, limit)
  }

  async leaveGroup(): Promise<void> {
    await GroupsService.leaveGroup(this.state.groupData._id)
    const wasActiveMember = this.state.userStatus === MemberStatus.ACTIVE
    if (wasActiveMember && typeof this.state.groupData?.membersCount === 'number') {
      this.state.groupData.membersCount = Math.max(0, this.state.groupData.membersCount - 1)
    }
    this.state.userStatus = null as any
    this.state.userGroup = {} as any
    this.state.role = null as any
    this.state.userMembershipAnswers = null as any
    if (this.state.channelList) {
      this.state.channelList = this.state.channelList.filter((channel: any) => channel.visibility !== ChannelVisibility.PRIVATE)
    }
  }
}

export const GroupState = new GroupsState()
